import { Link } from "react-router-dom";
import { Helmet } from "react-helmet";
import { Navigation } from "@/components/Navigation";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { UniversalLeadForm } from "@/components/forms/UniversalLeadForm";
import {
  ArrowRight,
  MapPin,
  ShieldCheck,
  Activity,
  Truck,
  Gauge,
  Wrench,
  AlertTriangle,
  Clock,
  Headphones,
  CheckCircle2,
} from "lucide-react";


const valueProps = [
  {
    icon: MapPin,
    title: "Real-Time Fleet Visibility",
    description:
      "Know where every vehicle, trailer, and asset is at any moment. Live location, trip history, geofences, and idle time — all in one place.",
  },
  {
    icon: ShieldCheck,
    title: "Hardware That Holds Up",
    description:
      "Ruggedized GPS, dash cams, and telematics built for the conditions your crews actually work in. Deployed in days, not months.",
  },
  {
    icon: Activity,
    title: "Operations-Grade Support",
    description:
      "Direct line to a team that runs the network — not a tier-one queue. When something breaks, you talk to someone who can fix it.",
  },
];

const problems = [
  {
    icon: AlertTriangle,
    title: "Blind spots cost money",
    description:
      "Unaccounted hours, off-route stops, and silent equipment mean lost revenue you only see at month-end.",
  },
  {
    icon: Clock,
    title: "Slow rollouts kill momentum",
    description:
      "Most providers take weeks to ship, install, and provision. Your fleet is moving today, not next quarter.",
  },
  {
    icon: Headphones,
    title: "Support tickets that go nowhere",
    description:
      "When a device drops offline, you don't have time to repeat your account number to three different reps.",
  },
];

const steps = [
  {
    step: "01",
    icon: Truck,
    title: "Tell us about your fleet",
    description:
      "Vehicle counts, asset types, and the data you actually need. We'll spec the right hardware and plans — no upsell theater.",
  },
  {
    step: "02",
    icon: Wrench,
    title: "Deploy in days",
    description:
      "Pre-provisioned devices ship ready to install. Plug-and-play OBD, hardwired GPS, or dash cams — whatever fits the asset.",
  },
  {
    step: "03",
    icon: Gauge,
    title: "Run with full visibility",
    description:
      "Live dashboards, alerts, and reports your dispatchers and ops leads will actually use. Backed by a team that picks up the phone.",
  },
];

const stats = [
  { value: "US", label: "BASED SUPPORT" },
  { value: "3-5 days", label: "Typical deployment window" },
  { value: "50 states", label: "Nationwide coverage" },
  { value: "1:1", label: "Direct account contact" },
];

const pricingTiers = [
  {
    name: "Basic Tracking",
    price: "$19.99",
    features: [
      "Real-time location tracking",
      "Basic AI call & text handling",
      "Simple lead capture",
      "Email support",
    ],
  },
  {
    name: "OBD Tracking",
    price: "$29.99",
    features: [
      "Everything in Basic Tracking",
      "OBD diagnostics & engine data",
      "Advanced AI routing & missed call text-back",
      "Appointment booking",
      "Priority support",
    ],
  },
  {
    name: "OBD with Theft Protect",
    price: "$34.99",
    features: [
      "Everything in OBD Tracking",
      "Backup power protection",
      "Advanced theft alerts",
      "Custom rules & reporting",
    ],
  },
  {
    name: "Dual Facing Camera",
    price: "$44.99",
    features: [
      "Everything in OBD with Theft Protect",
      "Live dual-facing camera feed + recording",
      "AI alerts (harsh braking, idling, distracted driving, etc.)",
      "Advanced analytics & reporting",
    ],
  },
];

const Home = () => {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <Helmet>
        <title>MuxSignal — Operational Reliability, Delivered</title>
        <meta
          name="description"
          content="Fleet tracking and asset visibility for operations teams that can't afford downtime. Ruggedized hardware, live data, and direct support."
        />
        <link rel="canonical" href="/" />
        <meta property="og:title" content="MuxSignal — Operational Reliability, Delivered" />
        <meta property="og:description" content="Fleet tracking and asset visibility for operations teams that can't afford downtime." />
        <meta property="og:url" content="/" />
      </Helmet>

      <Navigation />

      <main className="pt-16 sm:pt-20">
        {/* Hero - dark blue */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-32 bg-gradient-to-b from-[#0a1128] via-[#1a2332] to-[#0a1128] text-white">
          <div className="max-w-5xl mx-auto text-center">
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-primary/30 bg-primary/10 mb-6 shadow-[0_0_24px_-4px_hsl(214_100%_50%_/_0.5)]">
              <span className="h-2 w-2 rounded-full bg-primary animate-pulse" />
              <span className="text-xs font-semibold uppercase tracking-widest text-white">
                Fleet Tracking & Asset Visibility
              </span>
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight mb-6 text-white">
              Fleet Tracking & Asset Visibility
              <br />
              for Fleets That <span className="text-primary">Can't Sit Still.</span>
            </h1>
            <p className="text-lg lg:text-xl text-white/70 max-w-2xl mx-auto mb-10 leading-relaxed">
              MuxSignal gives operations teams the hardware, live visibility, and
              hands-on support they need to keep fleets moving and assets accounted for.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Button
                asChild
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base px-8 shadow-sm"
              >
                <Link to="/contact-us">
                  Get Started <ArrowRight className="ml-2 h-4 w-4" />
                </Link>
              </Button>
              <Button
                asChild
                variant="outline"
                size="lg"
                className="border-white/30 bg-white/5 text-white hover:bg-white/10 hover:text-white font-semibold text-base px-8"
              >
                <Link to="/contact-us">Talk to Us</Link>
              </Button>
            </div>
          </div>
        </section>


        {/* Stats strip - white */}
        <section className="px-4 sm:px-6 lg:px-8 py-12 bg-background border-y border-border">
          <div className="max-w-6xl mx-auto grid grid-cols-2 md:grid-cols-4 gap-6 text-center">
            {stats.map((s) => (
              <div key={s.label} className="glass-card p-5">
                <div className="text-2xl lg:text-3xl font-bold text-primary mb-1">{s.value}</div>
                <div className="text-xs lg:text-sm text-muted-foreground uppercase tracking-wide">{s.label}</div>
              </div>
            ))}
          </div>
        </section>

        {/* Value props - dark blue */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 lg:py-24 bg-gradient-to-b from-[#0a1128] via-[#1a2332] to-[#0a1128] text-white">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs font-semibold uppercase tracking-widest text-white mb-3 shadow-[0_0_24px_-4px_hsl(214_100%_50%_/_0.5)]">
                What you get
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                Built for fleets that can't sit still.
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8 lg:gap-12">
              {valueProps.map((vp) => (
                <div
                  key={vp.title}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 space-y-4 transition-all duration-300 hover:bg-white/10 hover:border-primary/40 hover:shadow-[0_0_40px_-8px_hsl(214_100%_50%_/_0.45)]"
                >
                  <div className="inline-flex items-center justify-center w-12 h-12 rounded-md bg-primary/15 border border-primary/30">
                    <vp.icon className="h-6 w-6 text-primary" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{vp.title}</h3>
                  <p className="text-white/70 leading-relaxed">{vp.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Problems - white */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 lg:py-24 bg-background">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Why teams switch
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground">
                The hidden cost of "good enough" tracking.
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {problems.map((p) => (
                <div key={p.title} className="glass-card p-6">
                  <p.icon className="h-6 w-6 text-primary mb-4" />
                  <h3 className="text-lg font-bold text-foreground mb-2">{p.title}</h3>
                  <p className="text-muted-foreground leading-relaxed text-sm">{p.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* How it works - dark blue */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 lg:py-24 bg-gradient-to-b from-[#0a1128] via-[#1a2332] to-[#0a1128] text-white">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-2xl mb-12">
              <span className="inline-flex items-center px-3 py-1 rounded-full border border-primary/30 bg-primary/10 text-xs font-semibold uppercase tracking-widest text-white mb-3 shadow-[0_0_24px_-4px_hsl(214_100%_50%_/_0.5)]">
                How it works
              </span>
              <h2 className="text-3xl lg:text-4xl font-bold text-white">
                From scoping to fully deployed in a single sprint.
              </h2>
            </div>
            <div className="grid md:grid-cols-3 gap-8">
              {steps.map((s) => (
                <div
                  key={s.step}
                  className="bg-white/5 backdrop-blur-md border border-white/10 rounded-2xl p-7 space-y-4 transition-all duration-300 hover:bg-white/10 hover:border-primary/40 hover:shadow-[0_0_40px_-8px_hsl(214_100%_50%_/_0.45)]"
                >
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-mono text-primary">{s.step}</span>
                    <div className="h-px flex-1 bg-white/15" />
                    <s.icon className="h-5 w-5 text-white/60" />
                  </div>
                  <h3 className="text-xl font-bold text-white">{s.title}</h3>
                  <p className="text-white/70 leading-relaxed">{s.description}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Fleet Tracking Pricing - white */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 lg:py-24 bg-background">
          <div className="max-w-6xl mx-auto">
            <div className="max-w-3xl mx-auto text-center mb-12">
              <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Pricing
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Fleet Tracking Pricing
              </h2>
              <p className="text-muted-foreground text-lg leading-relaxed">
                MuxSignal offers simple, transparent per-device pricing for AI-powered fleet tracking with unified communications. All plans include real-time GPS tracking, AI call/text handling, and dashboard access.
              </p>
            </div>

            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mb-10">
              {pricingTiers.map((tier, index) => (
                <div
                  key={index}
                  className="glass-card p-6 flex flex-col h-full"
                >
                  <h3 className="text-xl font-bold text-foreground mb-2">
                    {tier.name}
                  </h3>
                  <div className="mb-4">
                    <span className="text-4xl font-bold text-primary">{tier.price}</span>
                    <span className="text-muted-foreground">/device/mo</span>
                  </div>
                  <ul className="space-y-2 mb-6 flex-1">
                    {tier.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-sm">
                        <CheckCircle2 className="w-4 h-4 text-primary flex-shrink-0 mt-0.5" />
                        <span className="text-foreground/80">{feature}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              ))}
            </div>

            <div className="text-center space-y-6">
              <p className="text-muted-foreground">
                {"\n"}
              </p>
              <Button
                asChild
                size="lg"
                className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base px-8"
              >
                <Link to="/contact-us">
                  Contact Us to Get Started
                </Link>
              </Button>
            </div>
          </div>
        </section>

        {/* Contact Form - white */}
        <section className="px-4 sm:px-6 lg:px-8 py-16 lg:py-24 bg-background border-y border-border">
          <div className="max-w-2xl mx-auto">
            <div className="text-center mb-8">
              <p className="text-sm font-semibold uppercase tracking-widest text-primary mb-3">
                Get started
              </p>
              <h2 className="text-3xl lg:text-4xl font-bold text-foreground mb-4">
                Request a Consultation
              </h2>
              <p className="text-muted-foreground text-lg">
                Tell us about your fleet and we'll get you online.
              </p>
            </div>
            <UniversalLeadForm
              source="contact_form"
              variant="light"
              buttonText="Get Connected"
            />
          </div>
        </section>

        {/* Final CTA - dark blue */}
        <section className="px-4 sm:px-6 lg:px-8 py-20 lg:py-28 bg-gradient-to-b from-[#0a1128] via-[#1a2332] to-[#0a1128] text-white">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-3xl lg:text-5xl font-bold mb-6 text-white">
              Ready when you are.
            </h2>
            <p className="text-lg text-white/70 mb-10">
              Send us your fleet details and we'll get you online — no demo theater, no waiting on quotes.
            </p>
            <Button
              asChild
              size="lg"
              className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold text-base px-8"
            >
              <Link to="/contact-us">
                Get Started <ArrowRight className="ml-2 h-4 w-4" />
              </Link>
            </Button>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default Home;
